import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Pencil, Trash, TShirt, Package, ShoppingCart, MagnifyingGlass, Minus, UploadSimple, Image as ImageIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { OrderItemsDetail } from '../components/store/OrderItemsDetail';
import { PublicStoreOrders } from '../components/store/PublicStoreOrders';
import { ProductionList } from '../components/store/ProductionList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const productCategories = [
  { value: 'camiseta', label: 'Camiseta' },
  { value: 'shorts', label: 'Shorts' },
  { value: 'meias', label: 'Meias' },
  { value: 'acessórios', label: 'Acessórios' }
];

const sizes = ['PP', 'P', 'M', 'G', 'GG', 'XG'];
const colors = ['Branco', 'Preto', 'Azul', 'Vermelho', 'Laranja', 'Verde'];

const emptyProduct = {
  name: '',
  description: '',
  category: 'uniforme',
  price: '',
  sizes: [],
  colors: [],
  image_url: '',
  images: []
};

const ORDER_COLLECTION = 'orders';
const money = (v) => `$ ${(Number(v) || 0).toFixed(2)}`;

export default function Store() {
  const [products, setProducts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [orders, setOrders] = useState([]);
  const [publicOrders, setPublicOrders] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');
  const [search, setSearch] = useState('');
  
  // Product dialog
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [productForm, setProductForm] = useState(emptyProduct);
  
  // Order dialog
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderForm, setOrderForm] = useState({ student_id: '', items: [], notes: '', payment_mode: 'full' });
  const [combos, setCombos] = useState([]);
  const [comboDialogOpen, setComboDialogOpen] = useState(false);
  const [currentCombo, setCurrentCombo] = useState(null);
  const [comboForm, setComboForm] = useState({ name: '', description: '', price: '', items: [], is_active: true, images: [] });
  const [savingCombo, setSavingCombo] = useState(false);
  
  // Stock dialog
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockForm, setStockForm] = useState({ product_id: '', size: '', color: '', quantity: 0 });
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [productPhotos, setProductPhotos] = useState({});
  const [uploadingOrderZelle, setUploadingOrderZelle] = useState(null);
  const [colorInput, setColorInput] = useState('');
  const [imagePreviews, setImagePreviews] = useState({});
  const productImageRef = useRef(null);

  // Load product images
  const loadProductImages = async (products) => {
    const photos = {};
    for (const p of products) {
      if (p.image_url && p.image_url.startsWith('tia-karol-sports/')) {
        try {
          const resp = await axios.get(`${API}/files/${p.image_url}`, {
            withCredentials: true,
            responseType: 'blob'
          });
          photos[p.id] = URL.createObjectURL(resp.data);
        } catch {
          // ignore
        }
      } else if (p.image_url) {
        photos[p.id] = p.image_url;
      }
    }
    setProductPhotos(photos);
  };

  const fetchData = async () => {
    try {
      const [productsRes, inventoryRes, ordersRes, studentsRes, combosRes] = await Promise.all([
        axios.get(`${API}/products`, { withCredentials: true }),
        axios.get(`${API}/inventory`, { withCredentials: true }),
        axios.get(`${API}/orders`, { withCredentials: true }),
        axios.get(`${API}/students`, { withCredentials: true }),
        axios.get(`${API}/store-combos`, { withCredentials: true })
      ]);
      axios.get(`${API}/public/store/orders`, { withCredentials: true }).then((r) => setPublicOrders(r.data || [])).catch(() => {});
      setProducts(productsRes.data);
      setInventory(inventoryRes.data);
      setOrders(ordersRes.data);
      setStudents(studentsRes.data);
      setCombos(combosRes.data);
      loadProductImages(productsRes.data);
    } catch (e) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Product handlers
  const handleOpenCreateProduct = () => {
    setCurrentProduct(null);
    setColorInput('');
    setImagePreviews({});
    setProductForm(emptyProduct);
    setProductDialogOpen(true);
  };

  const handleOpenEditProduct = (product) => {
    setCurrentProduct(product);
    setColorInput('');
    const imgs = product.images && product.images.length ? product.images : (product.image_url ? [product.image_url] : []);
    setProductForm({
      name: product.name,
      description: product.description || '',
      category: product.category || 'uniforme',
      price: product.price.toString(),
      sizes: product.sizes || [],
      colors: product.colors || [],
      image_url: product.image_url || '',
      images: imgs
    });
    // Load previews for stored image paths
    const previews = {};
    imgs.forEach(async (path) => {
      if (path && path.startsWith('tia-karol-sports/')) {
        try {
          const resp = await axios.get(`${API}/files/${path}`, { withCredentials: true, responseType: 'blob' });
          setImagePreviews(prev => ({ ...prev, [path]: URL.createObjectURL(resp.data) }));
        } catch { /* ignore */ }
      } else if (path) {
        previews[path] = path;
      }
    });
    setImagePreviews(previews);
    setProductDialogOpen(true);
  };

  const handleOpenDeleteProduct = (product) => {
    setCurrentProduct(product);
    setDeleteDialogOpen(true);
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: productForm.name,
        description: productForm.description,
        category: productForm.category || 'uniforme',
        price: parseFloat(productForm.price),
        sizes: productForm.sizes,
        colors: productForm.colors,
        images: productForm.images,
        image_url: productForm.images[0] || ''
      };
      if (currentProduct) {
        await axios.put(`${API}/products/${currentProduct.id}`, payload, { withCredentials: true });
        toast.success('Produto atualizado com sucesso');
      } else {
        await axios.post(`${API}/products`, payload, { withCredentials: true });
        toast.success('Produto criado com sucesso');
      }
      setProductDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar produto');
    } finally {
      setSaving(false);
    }
  };

  // Color helpers
  const addColor = () => {
    const c = colorInput.trim();
    if (!c) return;
    if (!productForm.colors.includes(c)) {
      setProductForm(prev => ({ ...prev, colors: [...prev.colors, c] }));
    }
    setColorInput('');
  };
  const removeColor = (c) => setProductForm(prev => ({ ...prev, colors: prev.colors.filter(x => x !== c) }));
  const removeProductImage = (idx) => setProductForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));

  const handleProductImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingImage(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await axios.post(`${API}/upload`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setImagePreviews(prev => ({ ...prev, [data.path]: URL.createObjectURL(file) }));
        setProductForm(prev => ({ ...prev, images: [...prev.images, data.path] }));
      }
      toast.success('Imagem(ns) carregada(s)');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao fazer upload');
    } finally {
      setUploadingImage(false);
      if (productImageRef.current) productImageRef.current.value = '';
    }
  };

  const handleDeleteProduct = async () => {
    try {
      await axios.delete(`${API}/products/${currentProduct.id}`, { withCredentials: true });
      toast.success('Produto removido com sucesso');
      setDeleteDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error('Erro ao remover produto');
    }
  };

  // Stock handlers
  const handleOpenStock = (product) => {
    setStockForm({ product_id: product.id, size: '', color: '', quantity: 0, _sizes: product.sizes || [], _colors: product.colors || [] });
    setStockDialogOpen(true);
  };

  const handleSubmitStock = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/inventory`, {
        ...stockForm,
        quantity: parseInt(stockForm.quantity)
      }, { withCredentials: true });
      toast.success('Estoque atualizado');
      setStockDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error('Erro ao atualizar estoque');
    } finally {
      setSaving(false);
    }
  };

  // Order handlers
  const handleOpenCreateOrder = () => {
    setOrderForm({ student_id: '', items: [], notes: '', payment_mode: 'full' });
    setOrderDialogOpen(true);
  };

  const addItemToOrder = () => {
    setOrderForm({
      ...orderForm,
      items: [...orderForm.items, { product_id: '', size: '', color: '', quantity: 1 }]
    });
  };

  const removeItemFromOrder = (index) => {
    const newItems = orderForm.items.filter((_, i) => i !== index);
    setOrderForm({ ...orderForm, items: newItems });
  };

  const updateOrderItem = (index, field, value) => {
    const newItems = [...orderForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setOrderForm({ ...orderForm, items: newItems });
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (orderForm.items.length === 0) {
      toast.error('Adicione pelo menos um item');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/orders`, orderForm, { withCredentials: true });
      toast.success('Pedido criado com sucesso');
      setOrderDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar pedido');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { status }, { withCredentials: true });
      toast.success('Status atualizado');
      fetchData();
    } catch (e) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleSettleOrder = async (orderId, phase) => {
    try {
      await axios.post(`${API}/admin/order-settle`, { collection: ORDER_COLLECTION, order_id: orderId, phase }, { withCredentials: true });
      toast.success(phase === 'balance' ? 'Saldo marcado como pago' : phase === 'deposit' ? 'Sinal marcado como pago' : 'Pagamento registrado');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao registrar pagamento');
    }
  };

  const copyBalanceLink = (orderId) => {
    const url = `${window.location.origin}/pedido/${ORDER_COLLECTION}/${orderId}`;
    navigator.clipboard?.writeText(url)?.catch(() => {});
    toast.success('Link do saldo copiado!');
  };

  // Combo (Promoção) handlers
  const openCreateCombo = () => {
    setCurrentCombo(null);
    setComboForm({ name: '', description: '', price: '', items: [{ product_id: '', quantity: 1 }], is_active: true, images: [] });
    setComboDialogOpen(true);
  };
  const openEditCombo = (combo) => {
    setCurrentCombo(combo);
    setComboForm({
      name: combo.name,
      description: combo.description || '',
      price: String(combo.price ?? ''),
      items: (combo.items || []).map(i => ({ product_id: i.product_id, quantity: i.quantity || 1 })),
      is_active: combo.is_active !== false,
      images: []
    });
    setComboDialogOpen(true);
  };
  const addComboItem = () => setComboForm(prev => ({ ...prev, items: [...prev.items, { product_id: '', quantity: 1 }] }));
  const updateComboItem = (i, field, value) => setComboForm(prev => ({ ...prev, items: prev.items.map((it, idx) => idx === i ? { ...it, [field]: value } : it) }));
  const removeComboItem = (i) => setComboForm(prev => ({ ...prev, items: prev.items.filter((_, idx) => idx !== i) }));

  const handleSubmitCombo = async (e) => {
    e.preventDefault();
    const validItems = comboForm.items.filter(i => i.product_id).map(i => ({ product_id: i.product_id, quantity: parseInt(i.quantity) || 1 }));
    if (!comboForm.name || !comboForm.price || validItems.length < 1) {
      toast.error('Preencha nome, preço e ao menos 1 produto');
      return;
    }
    setSavingCombo(true);
    try {
      const payload = {
        name: comboForm.name,
        description: comboForm.description,
        price: parseFloat(comboForm.price),
        items: validItems,
        is_active: comboForm.is_active
      };
      if (currentCombo) {
        await axios.put(`${API}/store-combos/${currentCombo.id}`, payload, { withCredentials: true });
        toast.success('Promoção atualizada');
      } else {
        await axios.post(`${API}/store-combos`, payload, { withCredentials: true });
        toast.success('Promoção criada');
      }
      setComboDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar promoção');
    } finally {
      setSavingCombo(false);
    }
  };
  const handleDeleteCombo = async (combo) => {
    if (!window.confirm(`Remover a promoção "${combo.name}"?`)) return;
    try {
      await axios.delete(`${API}/store-combos/${combo.id}`, { withCredentials: true });
      toast.success('Promoção removida');
      fetchData();
    } catch {
      toast.error('Erro ao remover');
    }
  };
  const toggleCombo = async (combo) => {
    try {
      await axios.put(`${API}/store-combos/${combo.id}`, { is_active: !combo.is_active }, { withCredentials: true });
      fetchData();
    } catch {
      toast.error('Erro ao atualizar');
    }
  };

  const handleOrderZelleUpload = async (orderId, file) => {
    if (!file) return;
    setUploadingOrderZelle(orderId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/orders/zelle-receipt?order_id=${orderId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      toast.success('Comprovante enviado! Pagamento confirmado.');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar comprovante');
    } finally {
      setUploadingOrderZelle(null);
    }
  };

  const viewOrderReceipt = (orderId) => {
    const token = localStorage.getItem('access_token');
    window.open(`${API}/orders/zelle-receipt/${orderId}?auth=${token}`, '_blank');
  };

  const getStockForProduct = (productId) => {
    return inventory
      .filter(i => i.product_id === productId)
      .reduce((sum, i) => sum + i.quantity, 0);
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.category.toLowerCase().includes(search.toLowerCase())
  );

  const totalProducts = products.length;
  const totalStock = inventory.reduce((sum, i) => sum + i.quantity, 0);
  const pendingOrders = orders.filter(o => o.status === 'pendente').length;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="store-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Loja de Uniformes</h1>
            <p className="text-[#73706A] mt-1">Gerenciamento de produtos e pedidos</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#E85D04] rounded-lg flex items-center justify-center">
                <TShirt size={20} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-[#73706A]">Produtos</p>
                <p className="text-2xl font-semibold text-[#1A1A1A] font-['Outfit']">{totalProducts}</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0077B6] rounded-lg flex items-center justify-center">
                <Package size={20} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-[#73706A]">Itens em Estoque</p>
                <p className="text-2xl font-semibold text-[#1A1A1A] font-['Outfit']">{totalStock}</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FFB703] rounded-lg flex items-center justify-center">
                <ShoppingCart size={20} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-[#73706A]">Pedidos Pendentes</p>
                <p className="text-2xl font-semibold text-[#1A1A1A] font-['Outfit']">{pendingOrders}</p>
              </div>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#F5F2EB]">
            <TabsTrigger value="products" data-testid="tab-products">Produtos</TabsTrigger>
            <TabsTrigger value="promocoes" data-testid="tab-promocoes">Promoções</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Pedidos</TabsTrigger>
            <TabsTrigger value="production" data-testid="tab-production">Lista de Produção</TabsTrigger>
          </TabsList>

          <TabsContent value="production">
            {activeTab === 'production' && <ProductionList />}
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="relative max-w-md flex-1">
                <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#73706A]" />
                <Input
                  placeholder="Buscar produtos..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-products-input"
                />
              </div>
              <Button
                onClick={handleOpenCreateProduct}
                className="bg-[#E85D04] hover:bg-[#DC2F02] text-white"
                data-testid="add-product-btn"
              >
                <Plus size={20} className="mr-2" />
                Novo Produto
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <div key={i} className="h-64 bg-[#F5F2EB] rounded-lg animate-pulse" />
                ))
              ) : filteredProducts.length === 0 ? (
                <div className="col-span-full text-center py-12 bg-white border border-[#E6E2D8] rounded-lg">
                  <TShirt size={48} className="text-[#E6E2D8] mx-auto mb-4" />
                  <p className="text-[#73706A]">Nenhum produto cadastrado</p>
                </div>
              ) : (
                filteredProducts.map((product, index) => (
                  <div
                    key={product.id}
                    className="bg-white border border-[#E6E2D8] rounded-lg overflow-hidden card-hover"
                    data-testid={`product-card-${index}`}
                  >
                    <div className="h-32 bg-[#F5F2EB] flex items-center justify-center">
                      {productPhotos[product.id] ? (
                        <img src={productPhotos[product.id]} alt={product.name} className="h-full w-full object-contain" />
                      ) : product.image_url && !product.image_url.startsWith('tia-karol-sports/') ? (
                        <img src={product.image_url} alt={product.name} className="h-full w-full object-contain" />
                      ) : (
                        <TShirt size={48} className="text-[#E6E2D8]" />
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="badge badge-info capitalize text-xs">{product.category}</span>
                          <h3 className="font-medium text-[#1A1A1A] mt-1">{product.name}</h3>
                        </div>
                        <p className="font-semibold text-[#E85D04] text-lg">
                          $ {product.price.toFixed(2)}
                        </p>
                      </div>
                      {product.description && (
                        <p className="text-sm text-[#73706A] line-clamp-2 mb-3">{product.description}</p>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#73706A]">
                          Estoque: <strong>{getStockForProduct(product.id)}</strong>
                        </span>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenStock(product)}
                            className="text-[#2D6A4F] hover:text-[#2D6A4F] hover:bg-green-50"
                            data-testid={`stock-product-${index}`}
                          >
                            <Package size={18} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEditProduct(product)}
                            className="text-[#0077B6] hover:text-[#03045E] hover:bg-[#F5F2EB]"
                            data-testid={`edit-product-${index}`}
                          >
                            <Pencil size={18} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDeleteProduct(product)}
                            className="text-[#9B2226] hover:text-[#9B2226] hover:bg-red-50"
                            data-testid={`delete-product-${index}`}
                          >
                            <Trash size={18} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Promoções (Combos entre produtos) */}
          <TabsContent value="promocoes" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-['Outfit'] text-lg font-semibold text-[#1A1A1A]">Promoções (Combos)</h2>
                <p className="text-sm text-[#73706A]">Combos entre produtos com preço fechado. Aparecem na loja e no portal.</p>
              </div>
              <Button onClick={openCreateCombo} className="bg-[#E85D04] hover:bg-[#DC2F02]" data-testid="add-combo-btn">
                <Plus size={18} className="mr-2" /> Nova Promoção
              </Button>
            </div>

            {combos.length === 0 ? (
              <div className="text-center py-12 bg-white border border-[#E6E2D8] rounded-lg">
                <p className="text-[#73706A]">Nenhuma promoção criada ainda</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {combos.map((combo, ci) => (
                  <div key={combo.id} className="bg-white border border-[#E6E2D8] rounded-lg p-4" data-testid={`combo-card-${ci}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-[#1A1A1A]">{combo.name}</h3>
                        <span className={`badge ${combo.is_active ? 'badge-success' : 'badge-warning'} mt-1 inline-block`}>
                          {combo.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      <span className="text-xl font-bold text-[#E85D04]">$ {Number(combo.price).toFixed(2)}</span>
                    </div>
                    {combo.description && <p className="text-sm text-[#73706A] mt-2">{combo.description}</p>}
                    <ul className="text-sm text-[#1A1A1A] mt-2 space-y-0.5">
                      {combo.items.map((it, idx) => (
                        <li key={idx}>• {it.quantity}x {it.product_name || 'Produto'}</li>
                      ))}
                    </ul>
                    <div className="flex items-center gap-2 mt-4">
                      <Button variant="outline" size="sm" onClick={() => openEditCombo(combo)} data-testid={`edit-combo-${ci}`}>
                        <Pencil size={16} className="mr-1" /> Editar
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => toggleCombo(combo)} data-testid={`toggle-combo-${ci}`}>
                        {combo.is_active ? 'Desativar' : 'Ativar'}
                      </Button>
                      <Button variant="ghost" size="sm" className="text-[#9B2226]" onClick={() => handleDeleteCombo(combo)} data-testid={`delete-combo-${ci}`}>
                        <Trash size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-6">
            <div className="flex justify-end">
              <Button
                onClick={handleOpenCreateOrder}
                className="bg-[#E85D04] hover:bg-[#DC2F02] text-white"
                data-testid="add-order-btn"
              >
                <Plus size={20} className="mr-2" />
                Novo Pedido
              </Button>
            </div>

            <div className="bg-white border border-[#E6E2D8] rounded-lg overflow-hidden">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="w-8 h-8 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : orders.length === 0 ? (
                <div className="p-8 text-center text-[#73706A]">
                  Nenhum pedido registrado
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#F5F2EB]">
                        <th className="table-header">Aluno</th>
                        <th className="table-header">Itens</th>
                        <th className="table-header">Total</th>
                        <th className="table-header">Data</th>
                        <th className="table-header">Pagamento</th>
                        <th className="table-header">Status</th>
                        <th className="table-header text-right">Acoes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order, index) => (
                        <tr key={order.id} className="hover:bg-[#F5F2EB]/50 transition-colors" data-testid={`order-row-${index}`}>
                          <td className="table-cell font-medium text-[#1A1A1A]">{order.student_name}</td>
                          <td className="table-cell"><OrderItemsDetail items={order.items} testid={`order-items-${index}`} /></td>
                          <td className="table-cell font-medium text-[#2D6A4F]">
                            $ {order.total.toFixed(2)}
                          </td>
                          <td className="table-cell text-[#73706A]">
                            {new Date(order.created_at).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="table-cell">
                            <span className={`badge ${
                              order.status === 'entregue' ? 'badge-success' :
                              order.status === 'pendente' ? 'badge-warning' :
                              order.status === 'cancelado' ? 'badge-error' : 'badge-info'
                            }`}>
                              {order.status}
                            </span>
                          </td>
                          <td className="table-cell">
                            <span className={`badge ${order.payment_status === 'pago' ? 'badge-success' : order.payment_status === 'parcial' ? 'badge-info' : 'badge-warning'}`}>
                              {order.payment_status || 'pendente'}
                            </span>
                            {order.payment_method && order.payment_mode !== 'deposit' && (
                              <span className="text-xs text-[#73706A] ml-1">({order.payment_method})</span>
                            )}
                            {order.payment_mode === 'deposit' && (
                              <div className="mt-1 text-[11px] text-[#73706A] space-y-0.5" data-testid={`order-deposit-info-${index}`}>
                                <div>Sinal {money(order.deposit_amount)}: <span className={order.deposit_status === 'pago' ? 'text-[#2D6A4F] font-semibold' : 'text-[#9B2226]'}>{order.deposit_status}</span></div>
                                <div>Saldo {money(order.balance_amount)}: <span className={order.balance_status === 'pago' ? 'text-[#2D6A4F] font-semibold' : 'text-[#9B2226]'}>{order.balance_status}</span></div>
                              </div>
                            )}
                          </td>
                          <td className="table-cell text-right">
                            <div className="flex flex-col items-end gap-1">
                              <div className="flex items-center justify-end gap-1">
                              {order.payment_mode !== 'deposit' && (!order.payment_status || order.payment_status !== 'pago') ? (
                                <label className="cursor-pointer">
                                  <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                                    className="hidden"
                                    onChange={(e) => handleOrderZelleUpload(order.id, e.target.files[0])}
                                    data-testid={`order-zelle-upload-${index}`}
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-[#0077B6] hover:text-[#03045E] hover:bg-blue-50 pointer-events-none"
                                    disabled={uploadingOrderZelle === order.id}
                                    title="Enviar comprovante Zelle"
                                  >
                                    {uploadingOrderZelle === order.id ? (
                                      <div className="w-4 h-4 border-2 border-[#0077B6] border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                      <UploadSimple size={18} />
                                    )}
                                  </Button>
                                </label>
                              ) : null}
                              {order.zelle_receipt_path && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => viewOrderReceipt(order.id)}
                                  className="text-[#0077B6] hover:text-[#03045E] hover:bg-blue-50"
                                  data-testid={`view-order-receipt-${index}`}
                                  title="Ver comprovante Zelle"
                                >
                                  <ImageIcon size={18} />
                                </Button>
                              )}
                              <Select
                                value={order.status}
                                onValueChange={(value) => handleUpdateOrderStatus(order.id, value)}
                              >
                                <SelectTrigger className="w-28" data-testid={`order-status-${index}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pendente">Pendente</SelectItem>
                                  <SelectItem value="preparando">Preparando</SelectItem>
                                  <SelectItem value="entregue">Entregue</SelectItem>
                                  <SelectItem value="cancelado">Cancelado</SelectItem>
                                </SelectContent>
                              </Select>
                              </div>
                              {order.payment_mode === 'deposit' && order.payment_status !== 'pago' && (
                                <div className="flex items-center justify-end gap-1 flex-wrap">
                                  {order.deposit_status !== 'pago' && (
                                    <Button variant="outline" size="sm" className="h-7 text-[11px] text-[#2D6A4F] border-[#2D6A4F]" onClick={() => handleSettleOrder(order.id, 'deposit')} data-testid={`mark-deposit-paid-${index}`}>
                                      Sinal pago
                                    </Button>
                                  )}
                                  {order.balance_status !== 'pago' && (
                                    <Button variant="outline" size="sm" className="h-7 text-[11px] text-[#2D6A4F] border-[#2D6A4F]" onClick={() => handleSettleOrder(order.id, 'balance')} data-testid={`mark-balance-paid-${index}`}>
                                      Saldo pago
                                    </Button>
                                  )}
                                  <Button variant="outline" size="sm" className="h-7 text-[11px] text-[#0077B6] border-[#0077B6]" onClick={() => copyBalanceLink(order.id)} data-testid={`copy-balance-link-${index}`}>
                                    Link saldo
                                  </Button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <PublicStoreOrders orders={publicOrders} onRefresh={fetchData} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Product Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">
              {currentProduct ? 'Editar Produto' : 'Novo Produto'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitProduct} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nome *</Label>
                <Input
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  required
                  data-testid="product-name-input"
                />
              </div>
              <div className="col-span-2">
                <Label>Preço individual ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={productForm.price}
                  onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                  required
                  data-testid="product-price-input"
                />
                <p className="text-xs text-[#73706A] mt-1">Preço do produto avulso (sem promoção). Promoções são criadas na aba "Promoções".</p>
              </div>
              <div className="col-span-2">
                <Label>Descrição</Label>
                <textarea
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  className="input-field min-h-[60px] resize-none"
                  data-testid="product-description-input"
                />
              </div>
              <div className="col-span-2">
                <Label>Tamanhos disponíveis</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {sizes.map(size => (
                    <button
                      key={size}
                      type="button"
                      onClick={() => {
                        const newSizes = productForm.sizes.includes(size)
                          ? productForm.sizes.filter(s => s !== size)
                          : [...productForm.sizes, size];
                        setProductForm({ ...productForm, sizes: newSizes });
                      }}
                      className={`px-3 py-1 rounded border text-sm transition-colors ${
                        productForm.sizes.includes(size)
                          ? 'bg-[#E85D04] text-white border-[#E85D04]'
                          : 'border-[#E6E2D8] hover:border-[#E85D04]'
                      }`}
                      data-testid={`size-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-span-2">
                <Label>Cores disponíveis</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={colorInput}
                    onChange={(e) => setColorInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addColor(); } }}
                    placeholder="Ex: Rosa Neon, Azul Marinho..."
                    data-testid="product-color-input"
                  />
                  <Button type="button" variant="outline" onClick={addColor} data-testid="product-color-add-btn">
                    <Plus size={16} className="mr-1" /> Adicionar
                  </Button>
                </div>
                {productForm.colors.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {productForm.colors.map((c) => (
                      <span key={c} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#0077B6] text-white text-sm" data-testid={`product-color-chip-${c}`}>
                        {c}
                        <button type="button" onClick={() => removeColor(c)} className="hover:text-red-200" data-testid={`product-color-remove-${c}`}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="col-span-2">
                <div className="flex items-center justify-between">
                  <Label>Imagens do produto (cores)</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => productImageRef.current?.click()}
                    disabled={uploadingImage}
                    data-testid="product-image-upload-btn"
                  >
                    {uploadingImage ? (
                      <div className="w-4 h-4 border-2 border-[#E85D04] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <><UploadSimple size={16} className="mr-1" /> Adicionar fotos</>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-[#73706A] mt-1">Suba uma foto para cada cor disponível. O cliente verá todas na loja.</p>
                <input
                  ref={productImageRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  multiple
                  className="hidden"
                  onChange={handleProductImageUpload}
                  data-testid="product-image-file-input"
                />
                {productForm.images.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {productForm.images.map((img, idx) => (
                      <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#E6E2D8]" data-testid={`product-image-thumb-${idx}`}>
                        <img src={imagePreviews[img] || img} alt={`Cor ${idx + 1}`} className="w-full h-full object-contain bg-[#F5F2EB]" />
                        <button
                          type="button"
                          onClick={() => removeProductImage(idx)}
                          className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white text-xs flex items-center justify-center hover:bg-[#9B2226]"
                          data-testid={`product-image-remove-${idx}`}
                        >×</button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#73706A] text-center py-3">Nenhuma foto adicionada</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setProductDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#E85D04] hover:bg-[#DC2F02]"
                data-testid="save-product-btn"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Combo (Promoção) Dialog */}
      <Dialog open={comboDialogOpen} onOpenChange={setComboDialogOpen}>
        <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">
              {currentCombo ? 'Editar Promoção' : 'Nova Promoção'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitCombo} className="space-y-4 mt-2">
            <div>
              <Label>Nome da promoção *</Label>
              <Input value={comboForm.name} onChange={(e) => setComboForm({ ...comboForm, name: e.target.value })} placeholder="Ex: Combo Top + Regatão" required data-testid="combo-name-input" />
            </div>
            <div>
              <Label>Descrição</Label>
              <Input value={comboForm.description} onChange={(e) => setComboForm({ ...comboForm, description: e.target.value })} placeholder="Opcional" data-testid="combo-desc-input" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Produtos do combo *</Label>
                <Button type="button" variant="outline" size="sm" onClick={addComboItem} data-testid="combo-add-item-btn">
                  <Plus size={14} className="mr-1" /> Produto
                </Button>
              </div>
              <div className="space-y-2 mt-2">
                {comboForm.items.map((it, i) => (
                  <div key={i} className="flex items-end gap-2" data-testid={`combo-item-${i}`}>
                    <div className="flex-1">
                      <Label className="text-[11px]">Produto</Label>
                      <Select value={it.product_id} onValueChange={(v) => updateComboItem(i, 'product_id', v)}>
                        <SelectTrigger data-testid={`combo-item-product-${i}`}>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-20">
                      <Label className="text-[11px]">Qtd</Label>
                      <Input type="number" min="1" value={it.quantity} onChange={(e) => updateComboItem(i, 'quantity', e.target.value)} data-testid={`combo-item-qty-${i}`} />
                    </div>
                    <Button type="button" variant="ghost" size="sm" className="text-[#9B2226]" onClick={() => removeComboItem(i)} data-testid={`combo-item-remove-${i}`}>
                      <Trash size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Preço do combo ($) *</Label>
              <Input type="number" step="0.01" value={comboForm.price} onChange={(e) => setComboForm({ ...comboForm, price: e.target.value })} placeholder="Ex: 60.00" required data-testid="combo-price-input" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={comboForm.is_active} onChange={(e) => setComboForm({ ...comboForm, is_active: e.target.checked })} data-testid="combo-active-checkbox" />
              Ativo (visível na loja)
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setComboDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={savingCombo} className="bg-[#E85D04] hover:bg-[#DC2F02]" data-testid="save-combo-btn">
                {savingCombo ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stock Dialog */}
      <Dialog open={stockDialogOpen} onOpenChange={setStockDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">Atualizar Estoque</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitStock} className="space-y-4 mt-4">
            <div>
              <Label>Tamanho</Label>
              <Select
                value={stockForm.size}
                onValueChange={(value) => setStockForm({ ...stockForm, size: value })}
              >
                <SelectTrigger data-testid="stock-size-select">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {(stockForm._sizes?.length ? stockForm._sizes : sizes).map(size => (
                    <SelectItem key={size} value={size}>{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cor</Label>
              <Select
                value={stockForm.color}
                onValueChange={(value) => setStockForm({ ...stockForm, color: value })}
              >
                <SelectTrigger data-testid="stock-color-select">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {(stockForm._colors?.length ? stockForm._colors : colors).map(color => (
                    <SelectItem key={color} value={color}>{color}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantidade</Label>
              <Input
                type="number"
                value={stockForm.quantity}
                onChange={(e) => setStockForm({ ...stockForm, quantity: e.target.value })}
                data-testid="stock-quantity-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStockDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving || !stockForm.size || !stockForm.color}
                className="bg-[#E85D04] hover:bg-[#DC2F02]"
                data-testid="save-stock-btn"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Order Dialog */}
      <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">Novo Pedido</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitOrder} className="space-y-4 mt-4">
            <div>
              <Label>Aluno *</Label>
              <Select
                value={orderForm.student_id}
                onValueChange={(value) => setOrderForm({ ...orderForm, student_id: value })}
              >
                <SelectTrigger data-testid="order-student-select">
                  <SelectValue placeholder="Selecione um aluno" />
                </SelectTrigger>
                <SelectContent>
                  {students.map(student => (
                    <SelectItem key={student.id} value={student.id}>{student.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Itens</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addItemToOrder}
                  data-testid="add-order-item-btn"
                >
                  <Plus size={16} className="mr-1" /> Adicionar
                </Button>
              </div>
              
              {orderForm.items.length === 0 ? (
                <p className="text-sm text-[#73706A] text-center py-4">Nenhum item adicionado</p>
              ) : (
                <div className="space-y-3 max-h-48 overflow-y-auto">
                  {orderForm.items.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-[#F5F2EB] rounded-lg">
                      <Select
                        value={item.product_id}
                        onValueChange={(value) => updateOrderItem(index, 'product_id', value)}
                      >
                        <SelectTrigger className="flex-1" data-testid={`order-item-product-${index}`}>
                          <SelectValue placeholder="Produto" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={item.size}
                        onValueChange={(value) => updateOrderItem(index, 'size', value)}
                      >
                        <SelectTrigger className="w-20" data-testid={`order-item-size-${index}`}>
                          <SelectValue placeholder="Tam" />
                        </SelectTrigger>
                        <SelectContent>
                          {sizes.map(s => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={item.color}
                        onValueChange={(value) => updateOrderItem(index, 'color', value)}
                      >
                        <SelectTrigger className="w-24" data-testid={`order-item-color-${index}`}>
                          <SelectValue placeholder="Cor" />
                        </SelectTrigger>
                        <SelectContent>
                          {colors.map(c => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateOrderItem(index, 'quantity', parseInt(e.target.value) || 1)}
                        className="w-16"
                        data-testid={`order-item-qty-${index}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItemFromOrder(index)}
                        className="text-[#9B2226]"
                        data-testid={`remove-order-item-${index}`}
                      >
                        <Minus size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label>Forma de pagamento</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setOrderForm({ ...orderForm, payment_mode: 'full' })}
                  className={`p-3 rounded-lg border text-sm text-left transition-colors ${orderForm.payment_mode === 'full' ? 'border-[#E85D04] bg-orange-50' : 'border-[#E6E2D8]'}`}
                  data-testid="order-mode-full"
                >
                  <span className="font-medium">Valor total</span>
                  <span className="block text-[11px] text-[#73706A]">Pagamento integral</span>
                </button>
                <button
                  type="button"
                  onClick={() => setOrderForm({ ...orderForm, payment_mode: 'deposit' })}
                  className={`p-3 rounded-lg border text-sm text-left transition-colors ${orderForm.payment_mode === 'deposit' ? 'border-[#E85D04] bg-orange-50' : 'border-[#E6E2D8]'}`}
                  data-testid="order-mode-deposit"
                >
                  <span className="font-medium">Pré-reserva (50%)</span>
                  <span className="block text-[11px] text-[#73706A]">Metade agora, metade na entrega</span>
                </button>
              </div>
            </div>

            <div>
              <Label>Observações</Label>
              <textarea
                value={orderForm.notes}
                onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
                className="input-field min-h-[60px] resize-none"
                data-testid="order-notes-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOrderDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving || !orderForm.student_id || orderForm.items.length === 0}
                className="bg-[#E85D04] hover:bg-[#DC2F02]"
                data-testid="save-order-btn"
              >
                {saving ? 'Salvando...' : 'Criar Pedido'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">Confirmar exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-[#73706A] mt-2">
            Tem certeza que deseja remover o produto <strong>{currentProduct?.name}</strong>?
          </p>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteProduct}
              className="bg-[#9B2226] hover:bg-[#7d1c1f]"
              data-testid="confirm-delete-product-btn"
            >
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
